import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAIAlertsCheck } from '@/hooks/ai';
import { AIAlert } from '@/api/ai-api';
import { Button } from '@/components/ui/Button';

export function ClinicalAlertsPage() {
  const navigate = useNavigate();
  const { mutateAsync: check, isPending, data } = useAIAlertsCheck();

  const [patientId, setPatientId] = useState('');
  const [allergies, setAllergies] = useState('');
  const [currentMedications, setCurrentMedications] = useState('');
  const [proposed, setProposed] = useState('');
  const [pregnancy, setPregnancy] = useState(false);

  const handleCheck = async () => {
    if (!patientId || !proposed) return;
    await check({
      patientId,
      proposedMedications: proposed.split(',').map(s => s.trim()).filter(Boolean),
      patientAllergies: allergies ? allergies.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      currentMedications: currentMedications ? currentMedications.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      pregnancy,
    });
  };

  return (
    <div className="container max-w-5xl py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas Clínicos IA</h1>
          <p className="text-gray-500 mt-1">Agent 05 — Detecção de interações, alergias e contraindicações</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Dados do Paciente</h2>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Patient ID</label>
            <input
              type="text"
              value={patientId}
              onChange={e => setPatientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Alergias (separadas por vírgula)
            </label>
            <input
              type="text"
              value={allergies}
              onChange={e => setAllergies(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="penicilina, dipirona"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Medicações em uso (separadas por vírgula)
            </label>
            <input
              type="text"
              value={currentMedications}
              onChange={e => setCurrentMedications(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="losartana 50mg, metformina 850mg"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Medicações propostas (separadas por vírgula)
            </label>
            <input
              type="text"
              value={proposed}
              onChange={e => setProposed(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="amoxicilina 500mg"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={pregnancy}
              onChange={e => setPregnancy(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span>Gestante</span>
          </label>

          <Button onClick={handleCheck} disabled={isPending || !patientId || !proposed}>
            {isPending ? 'Verificando...' : 'Verificar Alertas'}
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">
            Alertas Detectados
            {data && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {data.length}
              </span>
            )}
          </h2>

          {data && data.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
              ✓ Nenhum alerta detectado — prescrição segura
            </div>
          )}

          {data && data.length > 0 && (
            <div className="space-y-3">
              {data.map(a => (
                <AlertCard key={a.id} alert={a} />
              ))}
            </div>
          )}

          {!data && (
            <div className="text-sm text-gray-500 text-center py-8">
              Configure os dados do paciente e clique em verificar
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertCard({ alert }: { alert: AIAlert }) {
  const sevColors: Record<string, string> = {
    CRITICAL: 'bg-red-50 border-red-300 text-red-900',
    HIGH: 'bg-orange-50 border-orange-300 text-orange-900',
    MODERATE: 'bg-yellow-50 border-yellow-300 text-yellow-900',
    LOW: 'bg-blue-50 border-blue-300 text-blue-900',
  };

  return (
    <div className={`rounded-lg border p-3 ${sevColors[alert.severity] || sevColors.LOW}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold uppercase">{alert.severity}</span>
        <span className="text-xs opacity-70">{alert.type.replace(/_/g, ' ')}</span>
      </div>
      <div className="text-sm font-semibold">{alert.title}</div>
      <div className="text-xs mt-1 opacity-90">{alert.description}</div>
    </div>
  );
}